import type { TourConfig } from '../types';
import { FormatIcon } from '../icons/FormatIcon';
import { ChangelogIcon } from '../icons/ChangelogIcon';
import { ScopeIcon } from '../icons/ScopeIcon';
import { SparkleIcon } from '../icons/SparkleIcon';
import { SubmitIcon } from '../icons/SubmitIcon';

/**
 * 5-step orientation tour shown the first time an authenticated user
 * enters edit mode (URL `?mode=edit`).
 *
 * Two anchors (`changelog`, `submitForReview`) are render-gated by the
 * branch having edits — when missing, the tour pauses silently
 * (TourOverlay renders nothing when rect is null) and resumes once the
 * user makes their first edit and those buttons mount.
 *
 * Copy is placeholder — design to confirm before merge. AI step text
 * matches the supplied mockup verbatim.
 */
export const editOnboardingTour: TourConfig = {
  id: 'edit-onboarding',
  gate: {
    storageKey: 'edit',
    shouldRun: ({ pathname, searchParams, viewportOk }) =>
      pathname.startsWith('/library') &&
      searchParams.get('mode') === 'edit' &&
      viewportOk,
  },
  steps: [
    {
      id: 'formatting',
      anchorName: 'formattingToolbar',
      title: 'Format your text',
      body: 'Use the toolbar to style headings, **bold**, italics, links and images.',
      icon: FormatIcon,
      side: 'bottom',
      align: 'start',
    },
    {
      id: 'changelog',
      anchorName: 'changelog',
      title: 'Track changes',
      body: 'See every edit on this branch in the **changelog**.',
      icon: ChangelogIcon,
      side: 'top',
      align: 'end',
    },
    {
      id: 'scope',
      anchorName: 'branchScope',
      title: 'Set visibility',
      body: 'Control who can see this branch — **private, org, team, or public**.',
      icon: ScopeIcon,
      side: 'top',
      align: 'center',
    },
    {
      id: 'ai',
      anchorName: 'editWithAI',
      title: 'Create with AI',
      body: 'Use **AI chat** to create and edit your ideas the way you want.',
      icon: SparkleIcon,
      side: 'bottom',
      align: 'end',
    },
    {
      id: 'submit',
      anchorName: 'submitForReview',
      title: 'Submit for review',
      body: '**Submit** your branch for review when you\'re done editing.',
      icon: SubmitIcon,
      side: 'top',
      align: 'end',
    },
  ],
};
